<template lang="pug">
  #manual-list(v-cloak)
    .box
      .box-header.with-border.register-header
        .title
          span マニュアル
        .buttons
          button.btn.back-btn(@click="gotoSupportList()" v-show="showBackButton" type="button") 戻る
      .box-body
        .manuals.d-flex.flex-wrap
          b-card.manual(v-if="_showFamilyAndWaiterServiceManual" v-for="(manual, index) in manuals" :key="manual.id")
            .manual-content(@click="showManual(manual.url)").d-flex.flex-nowrap.flex-row
              .icon
                img(:src="manual.iconUrl" :alt="manual.title")
              .title.d-flex.flex-nowrap.flex-column
                .title-text.align-self-start {{ manual.title }}
                .description.pre-line {{ manual.description }}
          b-card.manual(v-if="_showOtherOfficeServiceManual && otherOfficeServiceManual")
            .manual-content(@click="showManual(otherOfficeServiceManual.url)").d-flex.flex-nowrap.flex-row
              .icon
                img(:src="otherOfficeServiceManual.iconUrl" :alt="otherOfficeServiceManual.title")
              .title.d-flex.flex-nowrap.flex-column
                .title-text.align-self-start {{ otherOfficeServiceManual.title }}
                .description.pre-line {{ otherOfficeServiceManual.description }}

          Loading(ref="loading")

    PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import ManualsService, { ManualsGetModel } from "@/commons/services/Manuals";
import S3UrlService from "@/commons/services/S3Url";

import Loading from "@/pc/basics/Loading.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * マニュアル画面コンポーネント
 */
@Component({
  components: {
    Loading,
    PageLoading
  }
})
export default class ManualList extends Vue {
  /** マニュアルサービス */
  @Inject("ManualsService") manualsService!: ManualsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** マニュアル */
  manuals: Array<ManualsGetModel> = [];
  /** 他事業所連携用マニュアル（仮対応、マニュアルAPI、DBの見直しで削除する予定） */
  otherOfficeServiceManual: ManualsGetModel | null = null;

  /** 戻るボタン表示可否 */
  showBackButton: boolean = false;

  get _showFamilyAndWaiterServiceManual(): boolean {
    return this._useFamilyService || this._useWaiterService;
  }

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  get _useWaiterService(): boolean {
    return userStore.facility.useWaiterService && userStore.loginUser.permissions.functions.useWaiterService;
  }

  get _showOtherOfficeServiceManual(): boolean {
    return !this._useFamilyService && !this._useWaiterService && this._useOtherOfficeService;
  }

  get _useOtherOfficeService(): boolean {
    return userStore.facility.useOtherOfficeConnectionService && userStore.loginUser.permissions.functions.useOtherOfficeConnectionService;
  }

  /**
   * vue lifecycle created
   */
  public created(): void {
    // URLがサポートページからの遷移の場合は戻るボタンを表示する
    if (this.$route.path == PcUrl.SupportManualList) {
      this.showBackButton = true;
    } else {
      this.showBackButton = false;
    }

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 家族連絡、待機者管理サービスが有効の場合はマニュアル取得する
    if (this._showFamilyAndWaiterServiceManual) {
      const manuals: Array<ManualsGetModel> = await this.getManuals();
      this.manuals = manuals;
    }
    // 他事業所連携サービス蚤の場合は専用マニュアルを取得する
    else if (this._showOtherOfficeServiceManual) {
      const otherOfficeServiceManual: ManualsGetModel | null = await this.getOtherOfficeServiceManual();
      this.otherOfficeServiceManual = otherOfficeServiceManual;
    }

    this.isBusy = false;

    return;
  }

  /**
   * マニュアルを取得する
   */
  public async getManuals(): Promise<Array<ManualsGetModel>> {
    let manuals: Array<ManualsGetModel> = await this.manualsService.get(userStore);

    for (let manual of manuals) {
      manual.iconUrl = await this.convertS3Url(manual.iconUrl);
    }

    return manuals;
  }

  public async getOtherOfficeServiceManual(): Promise<ManualsGetModel> {
    const iconUrl: string = await this.convertS3Url(
      `https://s3-ap-northeast-1.amazonaws.com/${process.env.VUE_APP_IMAGE_BUCKETNAME}/manuals/icon/manual_otherOfficeConnections.png`
    );
    return {
      id: 0,
      plan: 0,
      orderIndex: 0,
      title: "操作マニュアル（他事業所連携）",
      description: "各機能の説明、管理システムの操作手順を説明します。操作方法が分からなくなった場合に、ご確認ください。",
      url: `https://s3-ap-northeast-1.amazonaws.com/${process.env.VUE_APP_IMAGE_BUCKETNAME}/manuals/pdf/HitomeQコネクト 操作マニュアル（他事業所連携）.pdf`,
      iconUrl: iconUrl,
      deleteFlag: false,
      created: 0,
      updated: 0
    };
  }

  /**
   * DBに保存されているS3URLを公開状態にし、公開状態にしたURLを返却する
   */
  public async convertS3Url(url: string): Promise<string> {
    // URLがない場合は空を返す
    if (!url) {
      return "";
    }

    const convertedUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, url);

    return convertedUrl;
  }

  /**
   * 別タブでマニュアルを表示する
   */
  public async showManual(url: string): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const convertedDrl: string = await this.convertS3Url(url);
    window.open(convertedDrl, "_blank");

    this.isBusy = false;

    return;
  }

  /**
   * 問い合わせ一覧画面へ遷移する
   */
  public gotoSupportList(): void {
    this.$router.push(PcUrl.SupportList);

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#manual-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-body {
    @include font(18px);

    .manuals {
      position: relative;
      min-height: 44px;
    }

    .manual {
      width: 610px;
      margin: 8px;
      cursor: pointer;

      .card-body {
        padding: 20px 20px 0 20px;
      }

      .manual-content {
        margin-left: 10px;
        max-width: 540px;

        .icon {
          margin-right: 24px;
          img {
            width: 138px;
            height: 129px;
          }
        }
        .title {
          margin-bottom: 16px;
          .title-text {
            @include bold-font(18px);
            margin-bottom: 20px;
          }
          .description {
            @include font(14px);
          }
        }
      }

      &:hover {
        background-color: $list-hover;
      }
    }
  }
}
</style>
